import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { FilterMode } from "@doitintl/cmp-models";
import ClearIcon from "@mui/icons-material/ClearRounded";
import {
  Autocomplete,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import debounce from "lodash/debounce";

import { filterDialogText } from "../../../../assets/texts/CloudAnalytics";
import { FilterModeOption } from "../../../../Pages/CloudAnalytics/allocations/types";
import { type MetadataOption } from "../../../../types";
import useFilterStore from "../hooks/useFilterState";
import { useValidateRegEx } from "../hooks/useValidateRegEx";
import { getPlural } from "../utils";

type FilterInputFieldsProps = {
  label: string;
  isFixed: boolean;
  selected: MetadataOption;
};

const blockedModes = [
  FilterModeOption.CONTAINS,
  FilterModeOption.DOES_NOT_CONTAIN,
  FilterModeOption.STARTS_WITH,
  FilterModeOption.ENDS_WITH,
  FilterModeOption.MATCHES_REGEX,
  FilterModeOption.DOES_NOT_MATCHES_REGEX,
];

const containedModes = [
  FilterModeOption.CONTAINS,
  FilterModeOption.DOES_NOT_CONTAIN,
  FilterModeOption.STARTS_WITH,
  FilterModeOption.ENDS_WITH,
];

export const FilterInputFields = ({ label, isFixed, selected }: FilterInputFieldsProps) => {
  const { plural } = getPlural(selected);
  const { disableRegexpFilter } = selected.data;
  const [error, setError] = useState(false);
  const { validateInputForRE2 } = useValidateRegEx(setError);
  const {
    mode,
    setMode,
    setFiltersStateChanged,
    inputValue,
    setInputValue,
    setFilters,
    selectedValues,
    setSelectedValues,
  } = useFilterStore();

  const isModeWithInput = useMemo(() => mode === FilterModeOption.IS || mode === FilterModeOption.IS_NOT, [mode]);

  const isModeWithChips = useMemo(
    () =>
      mode === FilterModeOption.CONTAINS ||
      mode === FilterModeOption.DOES_NOT_CONTAIN ||
      mode === FilterModeOption.STARTS_WITH ||
      mode === FilterModeOption.ENDS_WITH,
    [mode]
  );

  const isModeWithRegex = useMemo(
    () => mode === FilterModeOption.MATCHES_REGEX || mode === FilterModeOption.DOES_NOT_MATCHES_REGEX,
    [mode]
  );

  const modeMenuItems = useMemo(
    () =>
      Object.values(FilterModeOption)
        .filter((item) => {
          if (disableRegexpFilter) {
            return !blockedModes.includes(item);
          }
          return item;
        })
        .map((mode) => (
          <MenuItem key={mode} value={mode} data-cy={`dimension-mode-option-${mode}`}>
            {mode}
          </MenuItem>
        )),
    [disableRegexpFilter]
  );

  const handleModeChange = useCallback(
    (event) => {
      const selectedMode = event.target.value;
      setMode(selectedMode);
      if (containedModes.includes(selectedMode) && !isModeWithChips) {
        setFilters([]);
        setSelectedValues([]);
      }
      if ((selectedMode === FilterModeOption.IS || selectedMode === FilterModeOption.IS_NOT) && isModeWithChips) {
        setFilters([]);
        setSelectedValues([]);
      }
      if (
        (selectedMode === FilterModeOption.MATCHES_REGEX || selectedMode === FilterModeOption.DOES_NOT_MATCHES_REGEX) &&
        !isModeWithRegex
      ) {
        setInputValue("");
      }
      if ((selectedMode === FilterModeOption.IS || selectedMode === FilterModeOption.IS_NOT) && !isModeWithInput) {
        setInputValue("");
      }
      setFiltersStateChanged(true);
    },
    [
      isModeWithChips,
      isModeWithInput,
      isModeWithRegex,
      setFilters,
      setFiltersStateChanged,
      setInputValue,
      setMode,
      setSelectedValues,
    ]
  );

  useEffect(() => {
    setError(false);
    if (selected.mode === FilterMode.REGEXP) {
      setInputValue(selected._regexp ?? "");
    }
    if (selected.mode !== FilterMode.REGEXP && selected.mode !== FilterMode.IS) {
      setInputValue(selected._filter?.join(", ") ?? "");
      setSelectedValues(selected._filter ?? []);
    }
    if (selected.mode === FilterMode.IS) {
      setInputValue("");
    }
  }, [selected._filter, selected._regexp, selected.mode, setInputValue, setSelectedValues]);

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const debounceFn = debounce(validateInputForRE2, 1000);
      setError(false);
      setInputValue(event.target.value);
      await debounceFn(event.target.value);
      setFiltersStateChanged(true);
    },
    [setError, setFiltersStateChanged, setInputValue, validateInputForRE2]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      if (value === "") {
        return;
      }
      const valueList = [...selectedValues, value];
      setSelectedValues(valueList);
      setInputValue(valueList.join(", "));
      setFiltersStateChanged(true);
    },
    [setSelectedValues, selectedValues, setInputValue, setFiltersStateChanged]
  );
  return (
    <Stack direction="row" gap={2} alignItems="center">
      <FormControl sx={{ mt: 1, mb: isModeWithRegex ? 3 : 0, minWidth: 220 }} size="small">
        <InputLabel id="demo-select-small-label">{filterDialogText.CONDITION_LABEL}</InputLabel>
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          value={mode}
          fullWidth
          label={filterDialogText.CONDITION_LABEL}
          onChange={handleModeChange}
        >
          {modeMenuItems}
        </Select>
      </FormControl>
      {isModeWithInput && (
        <TextField
          label={isFixed ? label : `${label} values`}
          margin="normal"
          placeholder={filterDialogText.TYPE_TO_FILTER}
          data-testid="simple-filter"
          variant="outlined"
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
          }}
          fullWidth
          autoFocus
          slotProps={{
            input: {
              endAdornment: inputValue !== "" && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setInputValue("");
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },

            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
      {isModeWithRegex && (
        <TextField
          label={filterDialogText.REGEX_LABEL}
          helperText={error ? filterDialogText.REGEX_WRONG_SYNTAX : filterDialogText.FILTER_USING_REGEX(plural)}
          margin="normal"
          placeholder=".*"
          defaultValue={selected._regexp ?? ""}
          variant="outlined"
          value={inputValue}
          error={error}
          onChange={handleChange}
          fullWidth
          autoFocus
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
      {isModeWithChips && (
        <Autocomplete
          freeSolo
          multiple
          limitTags={2}
          autoFocus={false}
          value={selectedValues}
          onChange={(_, value) => {
            setSelectedValues(value);
            setInputValue(value.join(", "));
            setFiltersStateChanged(true);
          }}
          onBlur={handleBlur}
          clearOnBlur
          size="small"
          options={[]}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={"Values"}
              margin="normal"
              placeholder={filterDialogText.ADD_VALUES}
              data-testid="simple-filter"
              variant="outlined"
            />
          )}
        />
      )}
    </Stack>
  );
};
